import { useEffect, useState } from 'react';
import { Search, Plus, CreditCard as Edit2, Trash2, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { DiscountModal } from './DiscountModal';

interface Discount {
  id: string;
  title: string;
  description: string;
  discount_amount: string;
  category: string;
  vendor_id: string;
  is_active: boolean;
  expiry_date: string | null;
  created_at: string;
  brand?: string;
  promo_code?: string;
  valid_for: string[];
  image_url: string | null;
  terms: string | null;
}

export function DiscountsManagement() {
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<Discount | null>(null);

  useEffect(() => {
    loadDiscounts();
  }, []);

  const loadDiscounts = async () => {
    try {
      const { data, error } = await supabase
        .from('discounts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDiscounts(data || []);
    } catch (error) {
      console.error('Error loading discounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (discount: Discount) => {
    try {
      const { error } = await supabase
        .from('discounts')
        .update({ is_active: !discount.is_active })
        .eq('id', discount.id);

      if (error) throw error;
      await loadDiscounts();
    } catch (error) {
      console.error('Error toggling discount:', error);
      alert('Failed to update discount');
    }
  };

  const deleteDiscount = async (id: string) => {
    if (!confirm('Are you sure you want to delete this discount?')) return;

    try {
      const { error } = await supabase.from('discounts').delete().eq('id', id);
      if (error) throw error;
      await loadDiscounts();
    } catch (error) {
      console.error('Error deleting discount:', error);
      alert('Failed to delete discount');
    }
  };

  const filteredDiscounts = discounts.filter((discount) =>
    discount.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    discount.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    discount.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Discounts Management</h1>
          <p className="text-gray-600 mt-2">Manage community discounts and offers</p>
        </div>
        <button
          onClick={() => {
            setEditingDiscount(null);
            setShowModal(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} />
          <span>Add Discount</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search discounts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDiscounts.map((discount) => (
          <div key={discount.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{discount.title}</h3>
                  {discount.brand && (
                    <p className="text-sm text-gray-500 mb-2">{discount.brand}</p>
                  )}
                  <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 capitalize">
                    {discount.category}
                  </span>
                </div>
                <button
                  onClick={() => toggleActive(discount)}
                  className={`p-2 rounded-lg ${
                    discount.is_active
                      ? 'bg-green-100 text-green-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {discount.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{discount.description}</p>

              <div className="flex items-center justify-between mb-4">
                <div className="text-2xl font-bold text-blue-600">{discount.discount_amount}</div>
                {discount.promo_code && (
                  <div className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                    {discount.promo_code}
                  </div>
                )}
              </div>

              {discount.expiry_date && (
                <p className="text-xs text-gray-500 mb-4">
                  Expires: {new Date(discount.expiry_date).toLocaleDateString()}
                </p>
              )}

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    setEditingDiscount(discount);
                    setShowModal(true);
                  }}
                  className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <Edit2 size={16} />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => deleteDiscount(discount.id)}
                  className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <Trash2 size={16} />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredDiscounts.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
          <p className="text-gray-500">No discounts found</p>
        </div>
      )}

      <div className="mt-4 text-sm text-gray-600">
        Showing {filteredDiscounts.length} of {discounts.length} discounts
      </div>

      {showModal && (
        <DiscountModal
          discount={editingDiscount}
          onClose={() => {
            setShowModal(false);
            setEditingDiscount(null);
          }}
          onSave={() => {
            loadDiscounts();
          }}
        />
      )}
    </div>
  );
}
